import { formRegex, tLabel } from '@/core'
import { PostalCode } from '@/core/entities/values/PostalCode'
import { tMessage } from '@/core/form'

/**
 * 氏名（fullName）のバリデーション関数
 * 以下の条件をバリデーション：
 * 1. 最大50文字まで
 * 2. 特殊文字を含まない
 *
 * @param o - バリデーションオブジェクト（スキーマ定義から）
 * @param labelKey - ラベルの翻訳キー
 * @returns バリデーションチェーン
 */
export const validateFullName = (o: any, labelKey: string) => {
  return o
    .string()
    .max(50)
    .custom((value: string, helpers: any) => {
      const match = value?.match(/[=+\-@\t\r\n;,]+/)
      if (match) {
        return helpers.message({
          custom: tMessage('common:inputContainSpecialChars', {
            label: tLabel(labelKey),
          }),
        })
      }
      if (value && /^\s*$/.test(value)) {
        return helpers.message({
          custom: tMessage('common:inputCannotBeOnlySpaces', {
            label: tLabel(labelKey),
          }),
        })
      }
      return value
    })
    .label(tLabel(labelKey))
}

/**
 * 条件付きバリデーションを持つ氏名（fullName）のバリデーション関数
 * 他のフィールドの値に依存するバリデーションが必要な場合に使用
 *
 * @param o - バリデーションオブジェクト（スキーマ定義から）
 * @param condition - 条件オブジェクト（field: フィールド名, value: 値, labelKey: ラベルの翻訳キー）
 * @param flag - 追加のバリデーションのためのフラグフィールド名
 * @returns バリデーションチェーン
 */
export const validateFullNameConditional = (
  o: any,
  condition: { field: string; value: any; labelKey: string },
  flag: string
) => {
  return o.string().when(condition.field, {
    is: condition.value,
    then: validateFullName(o, condition.labelKey),
    otherwise: o.string().when(flag, {
      is: true,
      then: validateFullName(o, condition.labelKey),
      otherwise: o
        .string()
        .max(50)
        .optional()
        .allow(null, '')
        .custom((value: string, helpers: any) => {
          const match = value?.match(/[=+\-@\t\r\n;,]+/)
          if (match) {
            return helpers.message({
              custom: tMessage('common:inputContainSpecialChars', {
                label: tLabel(condition.labelKey),
              }),
            })
          }
          if (value && /^\s*$/.test(value)) {
            return helpers.message({
              custom: tMessage('common:inputCannotBeOnlySpaces', {
                label: tLabel(condition.labelKey),
              }),
            })
          }
          return value
        })
        .label(tLabel(condition.labelKey)),
    }),
  })
}

/**
 * 都道府県（prefecture）のバリデーション関数
 * 以下の条件をバリデーション：
 * 1. 最大150文字まで
 * 2. 特殊文字を含まない
 *
 * @param o - バリデーションオブジェクト（スキーマ定義から）
 * @param labelKey - ラベルの翻訳キー
 * @returns バリデーションチェーン
 */
export const validatePrefecture = (o: any, labelKey: string) => {
  return o
    .string()
    .max(150)
    .custom((value: string, helpers: any) => {
      const match = value?.match(/[=+\-@\t\r\n;,]+/)
      if (match) {
        return helpers.message({
          custom: tMessage('common:inputContainSpecialChars', {
            label: tLabel(labelKey),
          }),
        })
      }
      if (value && /^\s*$/.test(value)) {
        return helpers.message({
          custom: tMessage('common:inputCannotBeOnlySpaces', {
            label: tLabel(labelKey),
          }),
        })
      }
      return value
    })
    .label(tLabel(labelKey))
}

/**
 * 条件付きバリデーションを持つ都道府県（prefecture）のバリデーション関数
 * 他のフィールドの値に依存するバリデーションが必要な場合に使用
 *
 * @param o - バリデーションオブジェクト（スキーマ定義から）
 * @param condition - 条件オブジェクト（field: フィールド名, value: 値, labelKey: ラベルの翻訳キー）
 * @param flag - 追加のバリデーションのためのフラグフィールド名
 * @returns バリデーションチェーン
 */
export const validatePrefectureConditional = (
  o: any,
  condition: { field: string; value: any; labelKey: string },
  flag: string
) => {
  return o.string().when(condition.field, {
    is: condition.value,
    then: validatePrefecture(o, condition.labelKey),
    otherwise: o.string().when(flag, {
      is: true,
      then: validatePrefecture(o, condition.labelKey),
      otherwise: o
        .string()
        .max(150)
        .optional()
        .allow(null, '')
        .custom((value: string, helpers: any) => {
          // 特殊文字チェック
          const match = value?.match(/[=+\-@\t\r\n;,]+/)
          if (match) {
            return helpers.message({
              custom: tMessage('common:inputContainSpecialChars', {
                label: tLabel(condition.labelKey),
              }),
            })
          }
          // スペースチェック
          if (value && /^\s*$/.test(value)) {
            return helpers.message({
              custom: tMessage('common:inputCannotBeOnlySpaces', {
                label: tLabel(condition.labelKey),
              }),
            })
          }
          return value
        })
        .label(tLabel(condition.labelKey)),
    }),
  })
}

/**
 * 市区町村（city）のバリデーション関数
 * 以下の条件をバリデーション：
 * 1. 最大150文字まで
 * 2. 特殊文字を含まない
 *
 * @param o - バリデーションオブジェクト（スキーマ定義から）
 * @param labelKey - ラベルの翻訳キー
 * @returns バリデーションチェーン
 */
export const validateCity = (o: any, labelKey: string) => {
  return o
    .string()
    .max(150)
    .custom((value: string, helpers: any) => {
      const match = value?.match(/[=+\-@\t\r\n;,]+/)
      if (match) {
        return helpers.message({
          custom: tMessage('common:inputContainSpecialChars', {
            label: tLabel(labelKey),
          }),
        })
      }
      if (value && /^\s*$/.test(value)) {
        return helpers.message({
          custom: tMessage('common:inputCannotBeOnlySpaces', {
            label: tLabel(labelKey),
          }),
        })
      }
      return value
    })
    .label(tLabel(labelKey))
}

/**
 * 条件付きバリデーションを持つ市区町村（city）のバリデーション関数
 * 他のフィールドの値に依存するバリデーションが必要な場合に使用
 *
 * @param o - バリデーションオブジェクト（スキーマ定義から）
 * @param condition - 条件オブジェクト（field: フィールド名, value: 値, labelKey: ラベルの翻訳キー）
 * @param flag - 追加のバリデーションのためのフラグフィールド名
 * @returns バリデーションチェーン
 */
export const validateCityConditional = (
  o: any,
  condition: { field: string; value: any; labelKey: string },
  flag: string
) => {
  return o.string().when(condition.field, {
    is: condition.value,
    then: validateCity(o, condition.labelKey),
    otherwise: o.string().when(flag, {
      is: true,
      then: validateCity(o, condition.labelKey),
      otherwise: o
        .string()
        .max(150)
        .optional()
        .allow(null, '')
        .custom((value: string, helpers: any) => {
          // 特殊文字チェック
          const match = value?.match(/[=+\-@\t\r\n;,]+/)
          if (match) {
            return helpers.message({
              custom: tMessage('common:inputContainSpecialChars', {
                label: tLabel(condition.labelKey),
              }),
            })
          }
          // スペースチェック
          if (value && /^\s*$/.test(value)) {
            return helpers.message({
              custom: tMessage('common:inputCannotBeOnlySpaces', {
                label: tLabel(condition.labelKey),
              }),
            })
          }
          return value
        })
        .label(tLabel(condition.labelKey)),
    }),
  })
}

/**
 * 番地（line1）のバリデーション関数
 * 以下の条件をバリデーション：
 * 1. 最大150文字まで
 * 2. ハイフンで始まらない
 * 3. 特殊文字を含まない（ハイフンは除く）
 *
 * @param o - バリデーションオブジェクト（スキーマ定義から）
 * @param labelKey - ラベルの翻訳キー
 * @returns バリデーションチェーン
 */
export const validateLine1 = (o: any, labelKey: string) => {
  return o
    .string()
    .max(150)
    .custom((value: string, helpers: any) => {
      const match = value?.match(/^-.*$/)
      if (match) {
        return helpers.message({
          custom: tMessage('common:inputStartsWithSymbol', {
            symbol: match[0].slice(0, 1),
            label: tLabel(labelKey),
          }),
        })
      }
      const specialCharsRegex = value?.match(/[=+@\t\r\n;,]+/)
      if (specialCharsRegex) {
        return helpers.message({
          custom: tMessage('common:inputContainSpecialChars', {
            label: tLabel(labelKey),
          }),
        })
      }
      if (value && /^\s*$/.test(value)) {
        return helpers.message({
          custom: tMessage('common:inputCannotBeOnlySpaces', {
            label: tLabel(labelKey),
          }),
        })
      }
      return value
    })
    .label(tLabel(labelKey))
}

/**
 * 条件付きバリデーションを持つ番地（line1）のバリデーション関数
 * 他のフィールドの値に依存するバリデーションが必要な場合に使用
 *
 * @param o - バリデーションオブジェクト（スキーマ定義から）
 * @param condition - 条件オブジェクト（field: フィールド名, value: 値, labelKey: ラベルの翻訳キー）
 * @param flag - 追加のバリデーションのためのフラグフィールド名
 * @returns バリデーションチェーン
 */
export const validateLine1Conditional = (
  o: any,
  condition: { field: string; value: any; labelKey: string },
  flag: string
) => {
  return o.string().when(condition.field, {
    is: condition.value,
    then: validateLine1(o, condition.labelKey),
    otherwise: o.string().when(flag, {
      is: true,
      then: validateLine1(o, condition.labelKey),
      otherwise: o
        .string()
        .max(150)
        .optional()
        .allow(null, '')
        .custom((value: string, helpers: any) => {
          // 先頭に記号があるかチェック
          const match = value?.match(/^-.*$/)
          if (match) {
            return helpers.message({
              custom: tMessage('common:inputStartsWithSymbol', {
                symbol: match[0].slice(0, 1),
                label: tLabel(condition.labelKey),
              }),
            })
          }
          // 特殊文字チェック
          const specialCharsRegex = value?.match(/[=+@\t\r\n;,]+/)
          if (specialCharsRegex) {
            return helpers.message({
              custom: tMessage('common:inputContainSpecialChars', {
                label: tLabel(condition.labelKey),
              }),
            })
          }
          // スペースチェック
          if (value && /^\s*$/.test(value)) {
            return helpers.message({
              custom: tMessage('common:inputCannotBeOnlySpaces', {
                label: tLabel(condition.labelKey),
              }),
            })
          }
          return value
        })
        .label(tLabel(condition.labelKey)),
    }),
  })
}

/**
 * 建物名（line2）のバリデーション関数
 * 以下の条件をバリデーション：
 * 1. 最大150文字まで
 * 2. 特殊文字を含まない
 * 3. 任意項目
 *
 * @param o - バリデーションオブジェクト（スキーマ定義から）
 * @param labelKey - ラベルの翻訳キー
 * @returns バリデーションチェーン
 */
export const validateLine2 = (o: any, labelKey: string) => {
  return o
    .string()
    .optional()
    .allow('')
    .max(150)
    .custom((value: string, helpers: any) => {
      const match = value.match(/[=+\-@\t\r\n;,]+/)
      if (match) {
        return helpers.message({
          custom: tMessage('common:inputContainSpecialChars', {
            label: tLabel(labelKey),
          }),
        })
      }
      return value
    })
    .label(tLabel(labelKey))
}

/**
 * 条件付きバリデーションを持つ建物名（line2）のバリデーション関数
 * 他のフィールドの値に依存するバリデーションが必要な場合に使用
 *
 * @param o - バリデーションオブジェクト（スキーマ定義から）
 * @param condition - 条件オブジェクト（field: フィールド名, value: 値, labelKey: ラベルの翻訳キー）
 * @returns バリデーションチェーン
 */
export const validateLine2Conditional = (
  o: any,
  condition: { field: string; value: any; labelKey: string }
) => {
  return o.string().when(condition.field, {
    is: condition.value,
    then: validateLine2(o, condition.labelKey),
    otherwise: o
      .string()
      .optional()
      .allow(null, '')
      .max(150)
      .custom((value: string, helpers: any) => {
        // 特殊文字チェック
        const match = value?.match(/[=+\-@\t\r\n;,]+/)
        if (match) {
          return helpers.message({
            custom: tMessage('common:inputContainSpecialChars', {
              label: tLabel(condition.labelKey),
            }),
          })
        }
        return value
      })
      .label(tLabel(condition.labelKey)),
  })
}

/**
 * メールアドレス（email）のバリデーション関数
 * 以下の条件をバリデーション：
 * 1. 有効なメールアドレス形式
 * 2. 特殊文字（=, +, -, @）で始まらない
 * 3. 特殊文字（=, ;, タブ, 改行, キャリッジリターン, カンマ）を含まない
 *
 * @param o - バリデーションオブジェクト（スキーマ定義から）
 * @param labelKey - ラベルの翻訳キー
 * @returns バリデーションチェーン
 */
export const validateEmail = (o: any, labelKey: string) => {
  return o
    .string()
    .regex(formRegex.email)
    .custom((value: string, helpers: any) => {
      const match = value.match(/^([=+\-@])/)
      if (match) {
        return helpers.message({
          custom: tMessage('common:inputStartsWithSymbol', {
            symbol: match[1],
            label: tLabel(labelKey),
          }),
        })
      }
      const specialCharsRegex = /[=;\t\n\r,]/
      const specialCharsMatch = value.match(specialCharsRegex)
      if (specialCharsMatch) {
        return helpers.message({
          custom: tMessage('common:inputContainSpecialChars', {
            label: tLabel(labelKey),
          }),
        })
      }
      return value
    })
    .label(tLabel(labelKey))
}

/**
 * 表示名（displayName）のバリデーション関数
 * 以下の条件をバリデーション：
 * 1. 最大50文字まで
 * 2. 特殊文字を含まない
 *
 * @param o - バリデーションオブジェクト（スキーマ定義から）
 * @param labelKey - ラベルの翻訳キー
 * @returns バリデーションチェーン
 */
export const validateDisplayName = (o: any, labelKey: string) => {
  return o
    .string()
    .max(50)
    .custom((value: string, helpers: any) => {
      const match = value.match(/[=+\-@\t\r\n;,]+/)
      if (match) {
        return helpers.message({
          custom: tMessage('common:inputContainSpecialChars', {
            label: tLabel(labelKey),
          }),
        })
      }
      return value
    })
    .label(tLabel(labelKey))
}

/**
 * 電話番号（phoneNumber）のバリデーション関数
 * 以下の条件をバリデーション：
 * 1. 最大15文字まで
 * 2. スペースのみの入力でない
 *
 * @param o - バリデーションオブジェクト（スキーマ定義から）
 * @param labelKey - ラベルの翻訳キー
 * @returns バリデーションチェーン
 */
export const validatePhoneNumber = (o: any, labelKey: string) => {
  return o
    .string()
    .max(15)
    .custom((value: string, helpers: any) => {
      // スペースチェック
      if (value && /^\s*$/.test(value)) {
        return helpers.message({
          custom: tMessage('common:inputCannotBeOnlySpaces', {
            label: tLabel(labelKey),
          }),
        })
      }
      return value
    })
    .label(tLabel(labelKey))
}

/**
 * 条件付きバリデーションを持つ電話番号（phoneNumber）のバリデーション関数
 * 他のフィールドの値に依存するバリデーションが必要な場合に使用
 *
 * @param o - バリデーションオブジェクト（スキーマ定義から）
 * @param condition - 条件オブジェクト（field: フィールド名, value: 値, labelKey: ラベルの翻訳キー）
 * @param flag - 追加のバリデーションのためのフラグフィールド名
 * @returns バリデーションチェーン
 */
export const validatePhoneNumberConditional = (
  o: any,
  condition: { field: string; value: any; labelKey: string },
  flag: string
) => {
  return o.string().when(condition.field, {
    is: condition.value,
    then: validatePhoneNumber(o, condition.labelKey),
    otherwise: o.string().when(flag, {
      is: true,
      then: validatePhoneNumber(o, condition.labelKey),
      otherwise: o
        .string()
        .max(15)
        .optional()
        .allow(null, '')
        .custom((value: string, helpers: any) => {
          // スペースチェック
          if (value && /^\s*$/.test(value)) {
            return helpers.message({
              custom: tMessage('common:inputCannotBeOnlySpaces', {
                label: tLabel(condition.labelKey),
              }),
            })
          }
          return value
        })
        .label(tLabel(condition.labelKey)),
    }),
  })
}

/**
 * 条件付きバリデーションを持つ生年月日（birthday）のバリデーション関数
 * 他のフィールドの値に依存するバリデーションが必要な場合に使用
 *
 * @param o - バリデーションオブジェクト（スキーマ定義から）
 * @param condition - 条件オブジェクト（field: フィールド名, value: 値, labelKey: ラベルの翻訳キー）
 * @param flag - 追加のバリデーションのためのフラグフィールド名
 * @returns バリデーションチェーン
 */
export const validateBirthdayConditional = (
  o: any,
  condition: { field: string; value: any; labelKey: string },
  flag: string
) => {
  return o.date().when(condition.field, {
    is: condition.value,
    then: o.date().label(tLabel(condition.labelKey)),
    otherwise: o.date().when(flag, {
      is: true,
      then: o.date().label(tLabel(condition.labelKey)),
      otherwise: o
        .date()
        .optional()
        .allow(null, '')
        .label(tLabel(condition.labelKey)),
    }),
  })
}

/**
 * 条件付きバリデーションを持つ郵便番号（postalCode）のバリデーション関数
 * 他のフィールドの値に依存するバリデーションが必要な場合に使用
 *
 * @param o - バリデーションオブジェクト（スキーマ定義から）
 * @param condition - 条件オブジェクト（field: フィールド名, value: 値, labelKey: ラベルの翻訳キー）
 * @param flag - 追加のバリデーションのためのフラグフィールド名
 * @returns バリデーションチェーン
 */
export const validatePostalCodeConditional = (
  o: any,
  condition: { field: string; value: any; labelKey: string },
  flag: string
) => {
  return o.string().when(condition.field, {
    is: condition.value,
    then: o
      .string()
      .custom(PostalCode.createValidator(true))
      .label(tLabel(condition.labelKey)),
    otherwise: o.string().when(flag, {
      is: true,
      then: o
        .string()
        .custom(PostalCode.createValidator(true))
        .label(tLabel(condition.labelKey)),
      otherwise: o
        .string()
        .custom(PostalCode.createValidator(false))
        .optional()
        .allow(null, '')
        .label(tLabel(condition.labelKey)),
    }),
  })
}

/**
 * バナーの開始日・終了日の基本バリデーション
 * 開始日が終了日より後の場合はエラー
 *
 * @param value - バリデーション対象の値
 * @param helpers - バリデーションヘルパー
 * @param data - フォームデータ
 * @param startField - 開始日フィールド名
 * @param endField - 終了日フィールド名
 * @param messageKey - エラーメッセージキー
 * @returns バリデーション結果
 */
export const validateBannerDateOrder = (
  value: Date,
  helpers: any,
  data: any,
  startField: string,
  endField: string,
  messageKey: string
) => {
  if (!value) {
    return value
  }

  const startDate = data[startField] ? data[startField] : new Date()
  const endDate = data[endField]

  if (startDate && endDate && startDate > endDate) {
    return helpers.message({
      custom: tMessage(messageKey),
    })
  }

  return value
}

/**
 * 公開バナーと予約バナーの重複チェック
 * 公開バナーが予約バナーの期間内に開始する場合をチェック
 *
 * @param value - バリデーション対象の値
 * @param helpers - バリデーションヘルパー
 * @param data - フォームデータ
 * @param reservationStartField - 予約バナー開始日フィールド名
 * @param reservationEndField - 予約バナー終了日フィールド名
 * @param messageKey - エラーメッセージキー
 * @returns バリデーション結果
 */
export const validatePublicBannerOverlap = (
  value: Date,
  helpers: any,
  data: any,
  reservationStartField: string,
  reservationEndField: string,
  messageKey: string
) => {
  if (!value) {
    return value
  }

  const reservationStart =
    data[reservationStartField] && new Date(data[reservationStartField])
  const reservationEnd =
    data[reservationEndField] && new Date(data[reservationEndField])

  if (reservationStart) {
    if (reservationEnd) {
      // 公開バナーが予約バナーの期間内に開始する場合
      if (value > reservationStart && value < reservationEnd) {
        return helpers.message({
          custom: tMessage(messageKey),
        })
      }
    } else {
      // 公開バナーと予約バナーの重複チェック
      if (value > reservationStart) {
        return helpers.message({
          custom: tMessage(messageKey),
        })
      }
    }
  }

  return value
}

/**
 * 公開バナーの終了日と予約バナーの前後関係チェック
 * 公開バナーの期間が予約バナーの期間を完全に包含する場合をチェック
 *
 * @param value - バリデーション対象の値
 * @param helpers - バリデーションヘルパー
 * @param data - フォームデータ
 * @param startField - 開始日フィールド名
 * @param reservationStartField - 予約バナー開始日フィールド名
 * @param reservationEndField - 予約バナー終了日フィールド名
 * @param overlapMessageKey - 重複エラーメッセージキー
 * @param orderMessageKey - 前後関係エラーメッセージキー
 * @returns バリデーション結果
 */
export const validatePublicBannerEndDate = (
  value: Date,
  helpers: any,
  data: any,
  startField: string,
  reservationStartField: string,
  reservationEndField: string,
  overlapMessageKey: string,
  orderMessageKey: string
) => {
  if (!value) {
    return value
  }

  const startDate = data[startField]
  const reservationStart =
    data[reservationStartField] && new Date(data[reservationStartField])
  const reservationEnd =
    data[reservationEndField] && new Date(data[reservationEndField])

  if (reservationStart) {
    if (reservationEnd) {
      // 公開バナーの期間が予約バナーの期間を完全に包含する場合
      if (
        startDate &&
        startDate < reservationStart &&
        value > reservationStart
      ) {
        return helpers.message({
          custom: tMessage(overlapMessageKey),
        })
      }
      // 公開バナーの終了日が予約バナーの開始日より後の場合
      if (value > reservationStart) {
        return helpers.message({
          custom: tMessage(orderMessageKey),
        })
      }
    }
    return value
  }
  return value
}

/**
 * 予約バナーと公開バナーの重複チェック
 * 予約バナーが公開バナーの期間内に開始する場合をチェック
 *
 * @param value - バリデーション対象の値
 * @param helpers - バリデーションヘルパー
 * @param data - フォームデータ
 * @param publicStartField - 公開バナー開始日フィールド名
 * @param publicEndField - 公開バナー終了日フィールド名
 * @param messageKey - エラーメッセージキー
 * @returns バリデーション結果
 */
export const validateReservationBannerOverlap = (
  value: Date,
  helpers: any,
  data: any,
  publicStartField: string,
  publicEndField: string,
  messageKey: string
) => {
  if (!value) {
    return value
  }

  const publicStart = data[publicStartField] && new Date(data[publicStartField])
  const publicEnd = data[publicEndField] && new Date(data[publicEndField])

  if (publicStart) {
    if (publicEnd) {
      // 予約バナーが公開バナーの期間内に開始する場合
      if (value >= publicStart && value < publicEnd) {
        return helpers.message({
          custom: tMessage(messageKey),
        })
      }
    } else {
      // 予約バナーと公開バナーの重複チェック
      if (value >= publicStart) {
        return helpers.message({
          custom: tMessage(messageKey),
        })
      }
    }
  }

  return value
}

/**
 * 予約バナーの終了日と公開バナーの前後関係チェック
 *
 * @param value - バリデーション対象の値
 * @param helpers - バリデーションヘルパー
 * @param data - フォームデータ
 * @param startField - 開始日フィールド名
 * @param publicStartField - 公開バナー開始日フィールド名
 * @param publicEndField - 公開バナー終了日フィールド名
 * @param overlapMessageKey - 重複エラーメッセージキー
 * @param orderMessageKey - 前後関係エラーメッセージキー
 * @returns バリデーション結果
 */
export const validateReservationBannerEndDate = (
  value: Date,
  helpers: any,
  data: any,
  startField: string,
  publicStartField: string,
  publicEndField: string,
  overlapMessageKey: string,
  orderMessageKey: string
) => {
  if (!value) {
    return value
  }

  const startDate = data[startField]
  const publicStart = data[publicStartField] && new Date(data[publicStartField])
  const publicEnd = data[publicEndField] && new Date(data[publicEndField])

  if (publicStart) {
    if (publicEnd) {
      // 予約バナーの期間が公開バナーの期間を完全に包含する場合
      if (startDate && startDate < publicStart && value > publicStart) {
        return helpers.message({
          custom: tMessage(overlapMessageKey),
        })
      }
      // 公開バナーの開始日が予約バナーの終了日よりも後にある場合
      if (publicStart >= value) {
        return helpers.message({
          custom: tMessage(orderMessageKey),
        })
      }
    } else {
      // 無期限の場合は公開バナーの開始日を基準にする
      if (value >= publicStart) {
        return helpers.message({
          custom: tMessage(overlapMessageKey),
        })
      }
    }
  }

  return value
}

/**
 * 公開バナーの終了日時が設定されているかチェック
 * 予約バナーを作成する際に公開バナーに終了日時が必要
 *
 * @param value - バリデーション対象の値
 * @param helpers - バリデーションヘルパー
 * @param data - フォームデータ
 * @param publicStartField - 公開バナー開始日フィールド名
 * @param publicEndField - 公開バナー終了日フィールド名
 * @param messageKey - エラーメッセージキー
 * @returns バリデーション結果
 */
export const validatePublicBannerEndDateRequired = (
  value: Date,
  helpers: any,
  data: any,
  publicStartField: string,
  publicEndField: string,
  messageKey: string
) => {
  if (!value) {
    return value
  }

  const publicStart = data[publicStartField] && new Date(data[publicStartField])
  const publicEnd = data[publicEndField]

  // 公開バナーの開始日時が設定されているかチェック
  if (!publicStart || publicStart === '') {
    return helpers.message({
      custom: tMessage(messageKey),
    })
  }
  // 公開バナーの終了日時が設定されているかチェック
  if (!publicEnd || publicEnd === '') {
    return helpers.message({
      custom: tMessage(messageKey),
    })
  }

  return value
}
